import React, { useState, useRef, useCallback } from 'react';
import Header from '../components/Header';
import Chatbot from '../components/Chatbot';

const VideoPlayer = ({ src, testId }) => {
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const videoRef = useRef(null);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setStatus('loading');
    video.src = src;
    video.load();
  }, [src]);

  const onCanPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setStatus('playing');
    video.play().catch(() => {});
  }, []);

  const onError = useCallback(() => {
    if (status === 'idle') return;
    const video = videoRef.current;
    const msg = video?.error?.message || 'Could not load video';
    setErrorMsg(msg);
    setStatus('error');
  }, [status]);

  return (
    <div className="glass-card p-3 rounded-lg relative" style={{ minHeight: '300px' }}>
      {status === 'idle' && (
        <div
          className="absolute inset-3 flex flex-col items-center justify-center cursor-pointer bg-black/60 rounded z-10"
          onClick={handlePlay}
          data-testid={`${testId}-play-overlay`}
        >
          <svg className="w-20 h-20 text-orange-500 mb-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
          <span className="text-slate-300 text-lg font-medium">Click to Play Video</span>
        </div>
      )}
      {status === 'loading' && (
        <div className="absolute inset-3 flex flex-col items-center justify-center bg-black/60 rounded z-10 pointer-events-none">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-3"></div>
          <span className="text-slate-300 text-lg">Loading video...</span>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-3 flex flex-col items-center justify-center bg-black/80 rounded z-10">
          <span className="text-red-400 text-lg mb-2">Video could not be played</span>
          <span className="text-slate-400 text-sm mb-4 text-center max-w-md">{errorMsg}</span>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
            data-testid={`${testId}-direct-link`}
          >
            Open Video in New Tab
          </a>
        </div>
      )}
      <video
        ref={videoRef}
        controls
        playsInline
        className="w-full rounded"
        style={{ minHeight: '280px', maxHeight: '500px', background: '#000' }}
        data-testid={testId}
        onCanPlay={onCanPlay}
        onError={onError}
        onWaiting={() => { if (status === 'playing') setStatus('loading'); }}
        onPlaying={() => setStatus('playing')}
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

const PhotosPage = () => {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 noise-bg">
      <Header />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Buildings and Homes Section */}
          <div className="mb-20" id="buildings">
            <h2
              className="text-4xl md:text-5xl font-semibold tracking-tight mb-8 uppercase text-center text-orange-500"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
              data-testid="buildings-title"
            >
              Buildings and Homes
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/thdn5sq2_House%206.png"
                  alt="Steel Frame Home Construction"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-1"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/c37taekj_House%205.png"
                  alt="Steel Frame Building Under Construction"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-2"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/8wavp0tr_House%204.png"
                  alt="3D Steel Frame Design Model"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-3"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/u9k0vdmq_House%203.png"
                  alt="3D Steel Frame Design"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-4"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/t46dlkeb_3D-%20COMPUTER2.jpg"
                  alt="3D Metal Home Building Design Software"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-5"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/7p8ne4l2_house-6.webp"
                  alt="Aerial View of Steel Frame Construction"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="building-photo-6"
                />
              </div>
            </div>

            {/* Videos Section */}
            <div className="grid grid-cols-1 gap-6 mt-8">
              <VideoPlayer
                src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/g2m4tt0l_Sequence%2001_1.mp4"
                testId="building-video-1"
              />
            </div>
          </div>

          {/* Equipment Section */}
          <div id="equipment">
            <h2
              className="text-4xl md:text-5xl font-semibold tracking-tight mb-8 uppercase text-center text-orange-500"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
              data-testid="equipment-title"
            >
              Equipment
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_83a4b33c-3815-48cf-9291-b1b76ca07dab/artifacts/1ntg0yiq_751dbc75-f76b-4f45-a6d3-594cd57a3ae4.jpg"
                  alt="Steel Roll Forming Machine with Coil Feeder"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-new-1"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_83a4b33c-3815-48cf-9291-b1b76ca07dab/artifacts/kw0v1u5w_f68f6057-4ef7-4772-b743-e213853cac0e.jpg"
                  alt="Steel Frame Assembly in Manufacturing Facility"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-new-2"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_83a4b33c-3815-48cf-9291-b1b76ca07dab/artifacts/8x4apwcu_f68b553a-070b-47bf-b159-62bcd02c6bab.jpg"
                  alt="Steel Roof Truss Frame Close-up"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-new-3"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_83a4b33c-3815-48cf-9291-b1b76ca07dab/artifacts/jmvq7b0b_Scotpanel%202-7090G2.jpg"
                  alt="Scotpanel 7090 Steel Frame Manufacturing Machine"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-new-4"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_83a4b33c-3815-48cf-9291-b1b76ca07dab/artifacts/a3ebegbo_3dSTEEL%20PHOTO-.jpg"
                  alt="3D Steel Roll Forming Machine"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-2"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/yabqo12f_2025G1.webp"
                  alt="Knudson KFD 2025 G1 Roll Forming Machine"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-3"
                />
              </div>
              <div className="glass-card overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/jnimnie4_3D-%20Steel%20machines%201.webp"
                  alt="Steel Roll Forming Machines in Factory"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-4"
                />
              </div>
              <div className="glass-card overflow-hidden group md:col-span-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/jy1ep260_3D%20LOAD-1.png"
                  alt="Steel Frames Loaded on Delivery Truck"
                  className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid="equipment-photo-5"
                />
              </div>
            </div>

            {/* Equipment Videos Section */}
            <div className="grid grid-cols-1 gap-6 mt-8">
              <VideoPlayer
                src="https://customer-assets.emergentagent.com/job_d5366ac5-a01c-4f48-979c-933f1dd279c9/artifacts/1q5bg5nx_Recording_20260203_085538.mp4"
                testId="equipment-video-1"
              />
            </div>
          </div>
        </div>
      </div>
      <Chatbot />
    </div>
  );
};

export default PhotosPage;
